/**
 * Cash Flow: поступления, списания и чистый поток по месяцам и статьям ДДС.
 *
 * Остатка денег на счетах в данных нет — и раздел его не рисует.
 * Чистый поток за период не равен изменению остатка, если часть движений
 * 1С не прислала, поэтому «остаток на конец» отсюда не выводится.
 */

import { useFinance } from '../context'
import { indexBy, num, rowKey, useSettled } from '../data'
import { BarRows, ColumnChart } from '../charts'
import { Caveat, DataNotes, Delta, Kpi, Panel, SkeletonColumns, SkeletonRows } from '../parts'
import { originFrom } from '../Drawer'
import { useCashByItem, useCashBySection, useCashCoverage, useCashTrend } from '../queries'
import { compareLabel, labelOf, monthLabel, monthsOf, shortMonth } from '../period'
import { gapOf, missingOf, partialNote, valueIn } from '../coverage'
import { noCompareReason, salesEmpty } from '../frame'
import { MoneyDelta, money } from './Overview'
import { usePublishFreshness } from '../freshness'
import { deltaPercent, formatMoneyShort, formatPercent, toNumber } from '../../format'
import type { CSSProperties } from 'react'

/** Порядок и пояснения разделов ДДС (src/finance/classify.ts). */
const SECTIONS: Array<{ key: string; note: string }> = [
  { key: 'Операционная', note: 'Клиенты, поставщики, зарплата, налоги, аренда' },
  { key: 'Инвестиционная', note: 'Депозиты и проценты по ним' },
  { key: 'Финансовая', note: 'Кредиты, займы, средства собственника' },
  { key: 'Внутренние перемещения', note: 'Переводы между своими счетами и организациями — в потоке группы гасятся' },
  { key: 'Не размечено', note: 'Статьи без раздела — разметьте в админке «Финансовая разметка»' },
]

export const CASH_SERIES = [
  { key: 'inflow', label: 'Поступления', color: 'var(--series-1)' },
  { key: 'outflow', label: 'Списания', color: 'var(--flow-out)' },
]

export function Cash() {
  const ctx = useFinance()
  const { range, previousRange, trend, selection, branchIgnored, branchName, drill } = ctx
  const liveCoverage = useCashCoverage()
  const gap = gapOf(selection, range, liveCoverage, 'движения денег')
  const partial = partialNote(range, liveCoverage, 'движения денег')

  const sectionsLive = useCashBySection(range)
  const currentLive = useCashByItem(range)
  const beforeLive = useCashByItem(previousRange)
  const monthlyLive = useCashTrend(trend)
  usePublishFreshness([currentLive])

  // Период, прошлый период и тренд — одним набором с подписями своего выбора:
  // дельта не считается между новым периодом и прежним «прошлым».
  const settled = useSettled([currentLive, beforeLive, monthlyLive], {
    selection,
    noCompare: noCompareReason(selection, liveCoverage),
    trend,
    range,
    coverage: liveCoverage,
  })
  const [current, before, monthly] = settled.states
  const { noCompare, coverage } = settled.view
  const incomplete = Boolean(noCompare)
  const vs = compareLabel(settled.view.selection)

  const totals = current.data?.totals ?? {}
  const previous = before.data?.totals ?? {}
  const months = monthsOf(settled.view.trend)
  const byMonth = indexBy(monthly.data?.rows, 'period')
  const rangeMonths = new Set(monthsOf(settled.view.range))
  const rows = current.data?.rows ?? []
  const inflows = rows.filter((row) => num(row.inflow) > 0).sort((a, b) => num(b.inflow) - num(a.inflow))
  const outflows = rows.filter((row) => num(row.outflow) > 0).sort((a, b) => num(b.outflow) - num(a.outflow))
  const previousByItem = indexBy(before.data?.rows, 'cashflow_item')
  const net = toNumber(totals.net_flow)
  const inTotal = num(totals.inflow)
  const outTotal = num(totals.outflow)

  const compareNode = (node: React.ReactNode) => (noCompare ? <span className="muted">{noCompare}</span> : node)
  const emptyFor = (when: boolean, title: string, noun: string) => salesEmpty(ctx, when, title, { gap, noun })

  return (
    <div className="fin-stack">
      {branchIgnored('cashflow') && (
        <Caveat tone="warn">
          Движение денег в 1С не привязано к городу: статья ДДС и расчётный счёт общие для компании.
          Ниже — вся компания, фильтр «{branchName ?? 'выбранный город'}» к разделу не применяется.
        </Caveat>
      )}
      {partial && <Caveat tone="warn">{partial}</Caveat>}

      <div className="kpi-grid kpi-grid--3">
        <Kpi
          index={0}
          label="Поступления"
          memoryKey="cash.in"
          retry={current.retry}
          value={toNumber(totals.inflow)}
          format={money}
          state={current.status}
          delta={compareNode(<Delta value={deltaPercent(totals.inflow, previous.inflow)} label={vs} />)}
          spark={months.map((month) => valueIn(month, coverage, byMonth.get(month)?.inflow))}
        />
        <Kpi
          index={1}
          label="Списания"
          memoryKey="cash.out"
          retry={current.retry}
          value={toNumber(totals.outflow)}
          format={money}
          state={current.status}
          delta={compareNode(<Delta value={deltaPercent(totals.outflow, previous.outflow)} label={vs} invert />)}
          spark={months.map((month) => valueIn(month, coverage, byMonth.get(month)?.outflow))}
          sparkColor="var(--flow-out)"
        />
        <Kpi
          index={2}
          label="Чистый денежный поток"
          memoryKey="ov.net"
          retry={current.retry}
          value={net}
          format={money}
          state={current.status}
          tone={net !== null && net < 0 ? 'bad' : undefined}
          delta={compareNode(<MoneyDelta now={net} before={previous.net_flow} label={vs} />)}
          hint="Поступления минус списания за период"
          spark={months.map((month) => valueIn(month, coverage, byMonth.get(month)?.net_flow))}
          sparkColor="var(--ink-secondary)"
        />
      </div>

      {sectionsLive.status !== 'idle' && (
        <Panel
          index={1}
          title="Cash Flow по разделам"
          subtitle={`${labelOf(selection)} · операционная, инвестиционная и финансовая деятельность (разделы статей размечены у нас, в 1С их нет)`}
          states={[sectionsLive]}
          skeleton={<SkeletonRows rows={5} height={16} />}
          empty={emptyFor((sectionsLive.data?.rows.length ?? 0) === 0, 'Движений денег за период нет', 'движений')}
        >
          {() => {
            const bySection = new Map((sectionsLive.data?.rows ?? []).map((row) => [String(row.cf_section), row]))
            const shown = SECTIONS.filter((section) => bySection.has(section.key))
            return (
              <div className="table-wrap">
                <table className="fin-table">
                  <thead>
                    <tr>
                      <th>Раздел</th>
                      <th className="num">Поступления</th>
                      <th className="num">Выплаты</th>
                      <th className="num">Чистый поток</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((section, index) => {
                      const row = bySection.get(section.key)!
                      const sectionNet = num(row.net_flow)
                      return (
                        <tr key={section.key} className="cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                          <td>
                            <div className="strong">{section.key}</div>
                            <div className="tiny muted">{section.note}</div>
                          </td>
                          <td className="num mono">{formatMoneyShort(row.inflow)}</td>
                          <td className="num mono">{formatMoneyShort(row.outflow)}</td>
                          <td className={`num mono${sectionNet < 0 ? ' cell-negative' : ''}`}>{formatMoneyShort(sectionNet)}</td>
                        </tr>
                      )
                    })}
                    <tr className="pnl-line pnl-line--result">
                      <td>Итого</td>
                      <td className="num mono">{formatMoneyShort(sectionsLive.data?.totals.inflow)}</td>
                      <td className="num mono">{formatMoneyShort(sectionsLive.data?.totals.outflow)}</td>
                      <td className="num mono">{formatMoneyShort(sectionsLive.data?.totals.net_flow)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )
          }}
        </Panel>
      )}

      <Panel
        index={1}
        title="Поступления и списания по месяцам"
        subtitle="Вверх — поступления, вниз — списания, точка — чистый поток"
        states={[monthly]}
        skeleton={<SkeletonColumns height={240} />}
        empty={emptyFor(!monthly.data?.rows.some((row) => num(row.inflow) !== 0 || num(row.outflow) !== 0), 'Движений денег за 12 месяцев нет', 'движений')}
      >
        {() => (
          <ColumnChart
            ariaLabel="Поступления и списания по месяцам"
            height={240}
            diverging
            netLabel="Чистый поток"
            series={CASH_SERIES}
            format={formatMoneyShort}
            summaryTitle={labelOf(settled.view.selection)}
            points={months.map((month) => ({
              key: month,
              label: shortMonth(month),
              title: monthLabel(month),
              highlight: rangeMonths.has(month),
              missing: missingOf(month, coverage),
              values: [num(byMonth.get(month)?.inflow), num(byMonth.get(month)?.outflow)],
              net: toNumber(byMonth.get(month)?.net_flow),
            }))}
          />
        )}
      </Panel>

      <div className="fin-grid fin-grid--half">
        {[
          { title: 'Поступления по статьям', list: inflows, key: 'inflow' as const, total: inTotal, color: 'var(--series-1)', label: 'Поступления' },
          { title: 'Списания по статьям', list: outflows, key: 'outflow' as const, total: outTotal, color: 'var(--flow-out)', label: 'Списания' },
        ].map((block, blockIndex) => (
          <Panel
            key={block.key}
            index={2 + blockIndex}
            title={block.title}
            subtitle={`Доля от ${block.key === 'inflow' ? 'всех поступлений' : 'всех списаний'}${incomplete ? '' : ` · Δ ${vs}`}`}
            states={[current]}
            skeleton={<SkeletonRows rows={5} />}
            empty={emptyFor(
              block.list.length === 0,
              block.key === 'inflow' ? 'Поступлений за период нет' : 'Списаний за период нет',
              block.key === 'inflow' ? 'поступлений' : 'списаний',
            )}
          >
            {() => (
              <BarRows
                ariaLabel={block.title}
                series={[{ key: block.key, label: block.label, color: block.color }]}
                rows={block.list.map((row) => {
                  const key = rowKey(row, 'cashflow_item')
                  const delta = deltaPercent(row[block.key], previousByItem.get(key)?.[block.key])
                  return {
                    key,
                    label: String(row.cashflow_item ?? '—'),
                    sub: block.total > 0 ? `${formatPercent((num(row[block.key]) / block.total) * 100)} от итога` : undefined,
                    values: [num(row[block.key])],
                    aside: incomplete ? null : <Delta value={delta} invert={block.key === 'outflow'} />,
                  }
                })}
                onSelect={(row, element) => drill({ kind: 'cash_item', value: row.key, label: row.label }, originFrom(element))}
              />
            )}
          </Panel>
        ))}
      </div>

      <DataNotes states={[current, monthly]} />
      <p className="fin-footnote">
        Остатка денег на счетах в данных нет: 1С присылает движения, а не остатки. Поэтому раздел
        показывает только потоки за период.
      </p>
    </div>
  )
}
